import baseApi from './RootBaseApi';
import {
  AccountingExportEntriesListApiRaw,
  AccountingExportEntriesListNormalized,
  AccountingExportEntriesListParams,
  AccountingExportRunApiItem,
  AccountingExportRunDetailResponse,
  AccountingExportRunItem,
  AccountingExportRunsListApiRaw,
  AccountingExportRunsListNormalized,
  AccountingExportRunsListParams,
  InvoiceExportItem,
  InvoiceExportRun,
  InvoiceExportsListNormalized,
  RunAccountingExportPayload,
  AccountingExportCronConfig,
  AccountingExportCronConfigResponse,
} from '@/types/accounting-exports.types';

const mapRunApiItem = (
  item: AccountingExportRunApiItem
): AccountingExportRunItem => ({
  id: String(item.id),
  period_start: String(item.period_start || ''),
  period_end: String(item.period_end || ''),
  created_at: String(item.created_at || ''),
  triggered_by_user_id: item.triggered_by_user_id ?? null,
  status: String(item.status || ''),
  started_at: item.started_at ?? null,
  finished_at: item.finished_at ?? null,
  entry_count: Number(item.entry_count ?? 0),
  exported_entry_count: Number(item.exported_entry_count ?? 0),
  failed_entry_count: Number(item.failed_entry_count ?? 0),
  error_message: item.error_message ?? null,
});

export const AccountingExportsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAccountingExportRuns: builder.query<
      AccountingExportRunsListNormalized,
      AccountingExportRunsListParams
    >({
      query: (params) => ({
        url: 'accounting-exports/admin',
        method: 'GET',
        params,
      }),
      transformResponse: (
        response: unknown,
        _meta,
        arg
      ): AccountingExportRunsListNormalized => {
        const raw = response as AccountingExportRunsListApiRaw;
        const items = Array.isArray(raw?.items)
          ? raw.items.map(mapRunApiItem)
          : [];

        return {
          items,
          totalItems: Number(raw?.total ?? items.length),
          currentPage: Number(raw?.page ?? arg?.page ?? 1),
          totalPages: Number(raw?.totalPages ?? 1),
          pageLimit: Number((raw?.limit ?? arg?.limit ?? items.length) || 10),
        };
      },
      providesTags: ['AccountingExports'],
      keepUnusedDataFor: 0,
    }),

    runAccountingExport: builder.mutation<
      AccountingExportRunDetailResponse,
      RunAccountingExportPayload
    >({
      query: (payload) => ({
        url: 'accounting-exports/admin/run',
        method: 'POST',
        data: payload,
      }),
      transformResponse: (
        response: unknown
      ): AccountingExportRunDetailResponse => {
        const raw = response as AccountingExportRunApiItem & {
          response_status_code?: number | null;
        };
        return {
          ...mapRunApiItem(raw),
          response_status_code: raw.response_status_code ?? null,
        };
      },
      invalidatesTags: ['AccountingExports'],
    }),

    getAccountingExportRun: builder.query<
      AccountingExportRunDetailResponse,
      string
    >({
      query: (id) => ({
        url: `accounting-exports/admin/${id}`,
        method: 'GET',
      }),
      transformResponse: (
        response: unknown
      ): AccountingExportRunDetailResponse => {
        const raw = response as AccountingExportRunApiItem & {
          response_status_code?: number | null;
        };
        return {
          ...mapRunApiItem(raw),
          response_status_code: raw.response_status_code ?? null,
        };
      },
      providesTags: ['AccountingExports'],
      keepUnusedDataFor: 0,
    }),

    getAccountingExportEntries: builder.query<
      AccountingExportEntriesListNormalized,
      AccountingExportEntriesListParams
    >({
      query: ({ id, page, limit }) => ({
        url: `accounting-exports/admin/${id}/entries`,
        method: 'GET',
        params: {
          page,
          limit,
        },
      }),
      transformResponse: (
        response: unknown,
        _meta,
        arg
      ): AccountingExportEntriesListNormalized => {
        const raw = response as AccountingExportEntriesListApiRaw;
        const items = Array.isArray(raw?.items)
          ? raw.items.map((item) => ({
              id: String(item.id),
              idempotency_key: String(item.idempotency_key || ''),
              source_type: String(item.source_type || ''),
              source_id: String(item.source_id || ''),
              group_key: String(item.group_key || ''),
              occurred_at: String(item.occurred_at || ''),
              voucher_date: String(item.voucher_date || ''),
              voucher_description: String(item.voucher_description || ''),
              voucher_class: item.voucher_class ?? null,
              direction: String(item.direction || ''),
              linesum_type: String(item.linesum_type || ''),
              linesum: Number(item.linesum ?? 0),
              currency: String(item.currency || ''),
              tax_regime: item.tax_regime ?? null,
              product_type: item.product_type ?? null,
              account_number: item.account_number ?? null,
              vat_code: item.vat_code ?? null,
              vat_percent: item.vat_percent ?? null,
              exported_at: item.exported_at ?? null,
              export_error: item.export_error ?? null,
            }))
          : [];

        return {
          items,
          totalItems: Number(raw?.total ?? items.length),
          currentPage: Number(raw?.page ?? arg?.page ?? 1),
          totalPages: Number(raw?.totalPages ?? 1),
          pageLimit: Number((raw?.limit ?? arg?.limit ?? items.length) || 10),
        };
      },
      providesTags: ['AccountingExports'],
      keepUnusedDataFor: 0,
    }),

    executeAccountingExport: builder.mutation<void, string>({
      query: (id) => ({
        url: `accounting-exports/admin/${id}/execute`,
        method: 'POST',
      }),
      invalidatesTags: ['AccountingExports'],
    }),

    retryAccountingExport: builder.mutation<void, string>({
      query: (id) => ({
        url: `accounting-exports/admin/${id}/retry`,
        method: 'POST',
      }),
      invalidatesTags: ['AccountingExports'],
    }),

    getInvoiceExports: builder.query<
      InvoiceExportsListNormalized,
      { id: string; page?: number; limit?: number }
    >({
      query: ({ id, page, limit }) => ({
        url: `invoice-exports/admin/${id}`,
        method: 'GET',
        params: { page, limit },
      }),
      transformResponse: (
        response: unknown,
        _meta,
        arg
      ): InvoiceExportsListNormalized => {
        const raw = response as {
          run?: InvoiceExportRun;
          items?: InvoiceExportItem[];
          total?: number;
          page?: number;
          limit?: number;
          totalPages?: number;
        };
        const items: InvoiceExportItem[] = Array.isArray(raw?.items)
          ? raw.items.map((item) => ({
              id: String(item.id ?? ''),
              idempotency_key: String(item.idempotency_key ?? ''),
              item_type: String(item.item_type ?? ''),
              transaction_id: String(item.transaction_id ?? ''),
              occurred_at: String(item.occurred_at ?? ''),
              invoice_date: String(item.invoice_date ?? ''),
              exported_at: item.exported_at ?? null,
              export_error: item.export_error ?? null,
              request_xml: item.request_xml ?? null,
              response_xml: item.response_xml ?? null,
              netvisor_invoice_id: item.netvisor_invoice_id ?? null,
            }))
          : [];

        let runResult: InvoiceExportRun | undefined;
        if (raw?.run) {
          runResult = {
            id: String(raw.run.id ?? ''),
            period_start: String(raw.run.period_start ?? ''),
            period_end: String(raw.run.period_end ?? ''),
            created_at: String(raw.run.created_at ?? ''),
            triggered_by_user_id: raw.run.triggered_by_user_id ?? null,
            status: String(raw.run.status ?? ''),
            started_at: raw.run.started_at ?? null,
            finished_at: raw.run.finished_at ?? null,
            item_count: Number(raw.run.item_count ?? 0),
            exported_item_count: Number(raw.run.exported_item_count ?? 0),
            failed_item_count: Number(raw.run.failed_item_count ?? 0),
            error_message: raw.run.error_message ?? null,
            response_status_code: raw.run.response_status_code ?? null,
          };
        }

        return {
          items,
          run: runResult,
          totalItems: Number(raw?.total ?? items.length),
          currentPage: Number(raw?.page ?? arg?.page ?? 1),
          totalPages: Number(raw?.totalPages ?? 1),
          pageLimit: Number((raw?.limit ?? arg?.limit ?? items.length) || 10),
        };
      },
      providesTags: ['AccountingExports'],
      keepUnusedDataFor: 0,
    }),
    getAccountingExportCronConfig: builder.query<
      AccountingExportCronConfigResponse,
      void
    >({
      query: () => ({
        url: 'system-config/accounting-export-cron',
        method: 'GET',
      }),
      providesTags: ['AccountingExports'],
    }),
    updateAccountingExportCronConfig: builder.mutation<
      AccountingExportCronConfigResponse,
      AccountingExportCronConfig
    >({
      query: (data) => ({
        url: 'system-config/accounting-export-cron',
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['AccountingExports'],
    }),
  }),
});

export const {
  useGetAccountingExportRunsQuery,
  useRunAccountingExportMutation,
  useGetAccountingExportRunQuery,
  useGetAccountingExportEntriesQuery,
  useExecuteAccountingExportMutation,
  useRetryAccountingExportMutation,
  useGetInvoiceExportsQuery,
  useGetAccountingExportCronConfigQuery,
  useUpdateAccountingExportCronConfigMutation,
} = AccountingExportsService;
